import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// --- PDF Generator ---
const downloadReport = (data, type) => {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
 //console.log(data)
  // --- HEADER ---
  doc.setFontSize(20);
  doc.setTextColor(128, 0, 128);
  doc.text(
    type === "course" ? "Course Report Card" : "Student Report Card",
    pageW / 2,
    20,
    { align: "center" }
  );

  doc.setFontSize(12);
      const brandPart1 = "Study";
      const brandPart2 = "AK.ai";
      const brandPart1W = doc.getTextWidth(brandPart1);
      const totalBrandW = brandPart1W + doc.getTextWidth(brandPart2);
      let x = pageW - totalBrandW - 15;
      const y = 20;
      doc.setTextColor(128, 0, 128);
      doc.text(brandPart1, x, y);
      doc.setTextColor(0, 150, 0);
      doc.text(brandPart2, x + brandPart1W, y);

      if (type === "course") {
        // ✅ Student info (only once)
        const studentInfo = data[0];

        autoTable(doc, {
          startY: 30,
          theme: "grid",
          styles: { halign: "left", fontSize: 11 },
          headStyles: { fillColor: [128, 0, 128], textColor: 255 },
          body: [
            ["Name", `${studentInfo.firstName} ${studentInfo.lastName}`],
            ["Email", studentInfo.studentEmail],
            ["Course", studentInfo.courseName],
          ],
        });

        // ✅ Loop through tests
        data.forEach((result, index) => {
          // --- Test Title ---
          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 10,
            theme: "grid",
            styles: { halign: "left", fontSize: 11 },
            headStyles: { fillColor: [0, 150, 0], textColor: 255 },
            body: [["Test", result.testName]],
          });

          // --- Performance Summary ---
          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 5,
            theme: "grid",
            styles: { halign: "left", fontSize: 11 },
            headStyles: { fillColor: [0, 150, 0], textColor: 255 },
            body: [
              ["Score", result.score],
              ["Total Marks", result.totalMarks],
              ["Total Qs", result.totalQuestions],
              ["Attempted", result.attemptedQuestions],
              ["Correct", result.correctAnswers],
              ["Wrong", result.wrongAnswers],
              ["Unattempted", result.unattemptedQuestions],
            ],
          });

          // --- Analytics ---
          if (result.analyticData?.length > 0) {
            autoTable(doc, {
              startY: doc.lastAutoTable.finalY + 5,
              theme: "grid",
              styles: { fontSize: 11, halign: "left" },
              headStyles: { fillColor: [128, 0, 128], textColor: 255 },
              body: result.analyticData.map((a) => [a.key, a.data]),
            });
          }

          // --- Domain Performance ---
          if (result.domainPerformance?.length > 0) {
            autoTable(doc, {
              startY: doc.lastAutoTable.finalY + 5,
              theme: "grid",
              styles: { fontSize: 11, halign: "left" },
              headStyles: { fillColor: [0, 150, 0], textColor: 255 },
              body: result.domainPerformance.map((d) => [
                d.domainName,
                `${d.correctAnswers} / ${d.totalQuestion}`,
              ]),
            });
          }
        });
      } else {
        // ✅ Single test flow
        const result = data[0];

        autoTable(doc, {
          startY: 30,
          theme: "grid",
          styles: { halign: "left", fontSize: 11 },
          headStyles: { fillColor: [128, 0, 128], textColor: 255 },
          body: [
            ["Name", `${result.firstName} ${result.lastName}`],
            ["Email", result.studentEmail],
            ["Course", result.courseName],
            ["Test", result.testName],
          ],
        });

        // Performance summary
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 10,
          theme: "grid",
          styles: { halign: "left", fontSize: 11 },
          headStyles: { fillColor: [0, 150, 0], textColor: 255 },
          body: [
            ["Score", result.score],
            ["Total Marks", result.totalMarks],
            ["Total Qs", result.totalQuestions],
            ["Attempted", result.attemptedQuestions],
            ["Correct", result.correctAnswers],
            ["Wrong", result.wrongAnswers],
            ["Unattempted", result.unattemptedQuestions],
          ],
        });

        // Analytics
        if (result.analyticData?.length > 0) {
          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 10,
            theme: "grid",
            styles: { fontSize: 11, halign: "left" },
            headStyles: { fillColor: [128, 0, 128], textColor: 255 },
            body: result.analyticData.map((a) => [a.key, a.data]),
          });
        }

        // Domain performance
        if (result.domainPerformance?.length > 0) {
          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 10,
            theme: "grid",
            styles: { fontSize: 11, halign: "left" },
            headStyles: { fillColor: [0, 150, 0], textColor: 255 },
            body: result.domainPerformance.map((d) => [
              d.domainName,
              `${d.correctAnswers} / ${d.totalQuestion}`,
            ]),
          });
        }
      }

      // --- FOOTER ---
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(
        "Copyright © 2025 StudyAK, All rights reserved.",
        pageW / 2,
        pageH - 10,
        { align: "center" }
      );

      // --- SAVE FILE ---
      const fileName =
        type === "course"
          ? `${data[0].firstName}_${data[0].lastName}_course_report.pdf`
          : `${data[0].firstName}_${data[0].lastName}_test_report.pdf`;

      doc.save(fileName);
};

const AnalyticsDialog = ({ open, onClose, studentId, courseId, testId, type }) => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
    if (open) {
      let url = "";
      if (type === "course") {
        url = `${apiUrl}/api/user/${studentId}/course/${courseId}`;
      } else if (type === "test") {
        url = `${apiUrl}/api/user/${studentId}/test/${testId}`;
      }

      axios
        .get(url, {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: getBasicAuthHeader(),
          },
        })
        .then((res) => {
          if (res.data) {
            downloadReport(res.data, type);
          }
        })
        .catch((err) => {
          //console.error("Error fetching analytics:", err);
        })
        .finally(() => {
          onClose(); // Auto close after generating report
        });
    }
  }, [open, studentId, courseId, testId, type]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 30,
        }}
      >
        <CircularProgress />
        <Typography variant="subtitle1" style={{ marginTop: 15 }}>
          Report is getting generated...
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default AnalyticsDialog;
